import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle, Sparkles, ExternalLink } from "lucide-react";
import { Confetti } from "@/components/ui/confetti";
import { Button } from "@/components/ui/button";

interface SuccessAnimationProps {
  title?: string;
  message?: string;
  projectId?: string;
  onComplete?: (projectId?: string) => void;
}

export function SuccessAnimation({
  title = "Success!",
  message = "Your project has been set up successfully.",
  projectId,
  onComplete,
}: SuccessAnimationProps) {
  const [timeLeft, setTimeLeft] = useState(4);

  useEffect(() => {
    if (onComplete) {
      const timer = setTimeout(() => {
        onComplete(projectId);
      }, 4000);
      
      return () => clearTimeout(timer);
    }
  }, [onComplete, projectId]);

  // Countdown timer for UX
  useEffect(() => {
    if (timeLeft <= 0) return;
    
    const countdownTimer = setInterval(() => {
      setTimeLeft(prev => prev - 1);
    }, 1000);
    
    return () => clearInterval(countdownTimer);
  }, [timeLeft]);

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <Confetti particleCount={200} spread={180} duration={3000} />
      
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 20,
        }}
        className="bg-white dark:bg-gray-900 rounded-2xl p-8 max-w-md w-full text-center shadow-2xl"
      >
        <div className="mb-6 relative">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.3, type: "spring", stiffness: 300, damping: 20 }}
            className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full mx-auto flex items-center justify-center"
          >
            <CheckCircle className="w-12 h-12 text-green-600 dark:text-green-400" />
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7 }}
            className="absolute top-0 left-1/2 -translate-x-1/2"
          >
            <Sparkles className="w-8 h-8 text-yellow-400 dark:text-yellow-300 absolute -top-2 -right-8" />
            <Sparkles className="w-6 h-6 text-primary absolute -top-4 -left-6" />
            <Sparkles className="w-5 h-5 text-blue-400 absolute top-10 -right-10" />
          </motion.div>
        </div>
        
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="text-2xl font-bold mb-2 text-gray-900 dark:text-gray-100"
        >
          {title}
        </motion.h2>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-gray-600 dark:text-gray-300 mb-6"
        >
          {message}
        </motion.p>
        
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9 }}
          className="text-sm text-gray-500 dark:text-gray-400 mt-2"
        >
          Redirecting in {timeLeft} seconds...
        </motion.p>

        {projectId && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.1 }}
            className="mt-4"
          >
            <Button 
              variant="outline" 
              size="sm" 
              className="text-xs" 
              onClick={() => onComplete && onComplete(projectId)}
            >
              Go to project now
              <ExternalLink className="ml-1 h-3 w-3" />
            </Button>
          </motion.div>
        )}
        
        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={{ delay: 1, duration: 2.5 }}
          className="h-1 bg-primary/80 rounded-full"
        />
      </motion.div>
    </div>
  );
} 